// Ядро расчёта стоимости и валидации варианта путешествия.
import type {
  AppState,
  ConnectionInfo,
  CostBreakdown,
  FlightSegment,
  Money,
  Segment,
  TripVariant,
  TripWarning,
  VariantTotals,
} from '../types';
import { LONG_LAYOVER_MIN, RISKY_CONNECTION_MIN } from '../types';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * С какого разрыва «стыковка» перестаёт быть стыковкой. Сутки и больше в одном
 * аэропорту — это не ожидание рейса, а пребывание: человек прилетел сюда
 * отдыхать, путешествовать или по делам (типичный случай — точка назначения
 * «туда-обратно»). Показывать такому разрыву «стыковка 6 дней» — ошибка тона:
 * таймлайн называет его пребыванием, а предупреждение про ночёвку меняет
 * формулировку с «долгого ожидания» на «пребывание без ночёвки».
 */
export const STAY_THRESHOLD_MIN = 24 * 60;
import { createTranslator, type Translate } from '../i18n/translations';
import { convertMinor } from './money';
import { flightDurationMin, formatDuration, toUtcMillis } from './time';

/** Предупреждение с ключом сортировки по позиции на таймлайне варианта. */
interface OrderedWarning {
  order: number;
  warning: TripWarning;
}

function isFlight(seg: Segment): seg is FlightSegment {
  return seg.kind === 'flight';
}

/** Язык по умолчанию для вызовов вне UI (тесты, серверные утилиты). */
const defaultT: Translate = createTranslator('ru');

/**
 * Ночи «на весь промежуток»: от прилёта предыдущего рейса варианта до вылета
 * следующего, потолок 365. null — якорей нет: рядом нет рейсов, у них нет
 * таймзоны или порядок вывернут; вызывающий берёт запасные seg.nights.
 * Экспортируется отдельно: форма ночёвки показывает то же число заранее.
 */
export function wholeStayNights(
  segmentId: string,
  segIds: readonly string[],
  s: AppState,
): number | null {
  const idx = segIds.indexOf(segmentId);
  if (idx === -1) return null;

  let arrive: number | null = null;
  for (let i = idx - 1; i >= 0; i--) {
    const seg = s.library[segIds[i]];
    if (seg && isFlight(seg)) {
      arrive = toUtcMillis(seg.arriveLocal, seg.toTz);
      break;
    }
  }
  let depart: number | null = null;
  for (let i = idx + 1; i < segIds.length; i++) {
    const seg = s.library[segIds[i]];
    if (seg && isFlight(seg)) {
      depart = toUtcMillis(seg.departLocal, seg.fromTz);
      break;
    }
  }

  if (arrive === null || depart === null || depart <= arrive) return null;
  return Math.min(365, Math.max(1, Math.ceil((depart - arrive) / DAY_MS)));
}

/** Ночи блока с учётом «на весь промежуток». */
function effectiveNights(
  seg: Extract<Segment, { kind: 'lodging' }>,
  segIds: readonly string[],
  s: AppState,
): number {
  if (!seg.wholeStay) return seg.nights;
  return wholeStayNights(seg.id, segIds, s) ?? seg.nights;
}

export function computeVariantTotals(
  v: TripVariant,
  s: AppState,
  t: Translate = defaultT,
): VariantTotals {
  const segIds = v.segmentIds;
  const posOf = new Map<string, number>();
  segIds.forEach((id, i) => posOf.set(id, i));
  const inVariant = new Set(segIds);

  const segments: Segment[] = [];
  for (const id of segIds) {
    const seg = s.library[id];
    if (seg) segments.push(seg);
  }

  let converted = false;
  const conv = (m: Money): number => {
    const result = convertMinor(m, s.displayCurrency, s.currencies);
    if (m.currency !== s.displayCurrency) converted = true;
    return result;
  };

  const orderedWarnings: OrderedWarning[] = [];
  const pushWarning = (order: number, warning: TripWarning): void => {
    orderedWarnings.push({ order, warning });
  };

  // ── Проход 1: round_trip_full пары ──────────────────────────────────────
  // Конвертированная цена (до умножения на travelers), которую нужно добавить
  // за конкретный flight-сегмент с priceScope === 'round_trip_full'.
  const rtOverride = new Map<string, number>();
  const handledRt = new Set<string>();

  for (const seg of segments) {
    if (!isFlight(seg) || seg.priceScope !== 'round_trip_full') continue;
    if (handledRt.has(seg.id)) continue;
    handledRt.add(seg.id);

    const partnerRaw = seg.linkedTo ? s.library[seg.linkedTo] : undefined;
    const partner =
      partnerRaw && isFlight(partnerRaw) && partnerRaw.priceScope === 'round_trip_full'
        ? partnerRaw
        : undefined;
    const partnerPresent = !!partner && inVariant.has(partner.id);

    if (!partner || !partnerPresent) {
      // Одиночный член пары (или linkedTo ведёт в никуда) — полная цена включается,
      // но с предупреждением.
      const fullConverted = conv(seg.price);
      rtOverride.set(seg.id, fullConverted);
      pushWarning(posOf.get(seg.id) ?? 0, {
        code: 'rt_full_unpaired',
        level: 'warn',
        message: t('calcwarn.rt_full_unpaired'),
        segmentId: seg.id,
      });
      continue;
    }

    handledRt.add(partner.id);
    const segConverted = conv(seg.price);
    const partnerConverted = conv(partner.price);

    let canonicalId: string;
    if (segConverted !== partnerConverted) {
      canonicalId = segConverted > partnerConverted ? seg.id : partner.id;
    } else {
      canonicalId = seg.id < partner.id ? seg.id : partner.id;
    }
    const otherId = canonicalId === seg.id ? partner.id : seg.id;
    rtOverride.set(canonicalId, canonicalId === seg.id ? segConverted : partnerConverted);
    rtOverride.set(otherId, 0);
  }

  // ── Проход 2: стоимость по сегментам ────────────────────────────────────
  const breakdown: CostBreakdown = {
    flight: 0,
    transfer: 0,
    lodging: 0,
    taxi: 0,
    food: 0,
    baggage: 0,
    misc: 0,
  };
  const perSegmentMinor: Record<string, number> = {};

  for (const seg of segments) {
    switch (seg.kind) {
      case 'flight': {
        const base =
          seg.priceScope === 'round_trip_full'
            ? rtOverride.get(seg.id) ?? conv(seg.price)
            : conv(seg.price);
        const amount = base * s.travelers;
        perSegmentMinor[seg.id] = amount;
        breakdown.flight += amount;
        break;
      }
      case 'transfer': {
        const amount = conv(seg.price) * (seg.perPerson ? s.travelers : 1);
        perSegmentMinor[seg.id] = amount;
        breakdown.transfer += amount;
        break;
      }
      case 'lodging': {
        const amount =
          conv(seg.pricePerNight) * effectiveNights(seg, segIds, s) * (seg.perPerson ? s.travelers : 1);
        perSegmentMinor[seg.id] = amount;
        breakdown.lodging += amount;
        break;
      }
      case 'taxi':
      case 'food':
      case 'misc': {
        const amount = conv(seg.price) * seg.quantity * s.travelers;
        perSegmentMinor[seg.id] = amount;
        breakdown[seg.kind] += amount;
        break;
      }
      case 'baggage': {
        const amount = conv(seg.price) * s.travelers;
        perSegmentMinor[seg.id] = amount;
        breakdown.baggage += amount;
        if (!inVariant.has(seg.flightId)) {
          pushWarning(posOf.get(seg.id) ?? 0, {
            code: 'orphan_baggage',
            level: 'error',
            message: t('calcwarn.orphan_baggage'),
            segmentId: seg.id,
          });
        }
        break;
      }
    }
  }

  const totalMinor =
    breakdown.flight +
    breakdown.transfer +
    breakdown.lodging +
    breakdown.taxi +
    breakdown.food +
    breakdown.misc;
  const totalWithBaggageMinor = totalMinor + breakdown.baggage;

  // ── Время ────────────────────────────────────────────────────────────────
  const flights = segments.filter(isFlight);
  let flightTimeMin = 0;
  for (const f of flights) {
    const dur = flightDurationMin(f);
    if (dur === null) {
      pushWarning(posOf.get(f.id) ?? 0, {
        code: 'missing_tz',
        level: 'warn',
        message: t('calcwarn.missing_tz'),
        segmentId: f.id,
      });
      continue;
    }
    flightTimeMin += dur;
  }

  let doorToDoorMin = 0;
  if (flights.length >= 1) {
    const first = flights[0];
    const last = flights[flights.length - 1];
    const departUtc = toUtcMillis(first.departLocal, first.fromTz);
    const arriveUtc = toUtcMillis(last.arriveLocal, last.toTz);
    if (departUtc !== null && arriveUtc !== null) {
      doorToDoorMin = Math.round((arriveUtc - departUtc) / 60000);
    }
  }

  const nights = segments
    .filter((seg): seg is Extract<Segment, { kind: 'lodging' }> => seg.kind === 'lodging')
    .reduce((sum, seg) => sum + effectiveNights(seg, segIds, s), 0);

  // ── Стыки между соседними рейсами ────────────────────────────────────────
  const connections: ConnectionInfo[] = [];
  for (let i = 0; i < flights.length - 1; i++) {
    const a = flights[i];
    const b = flights[i + 1];
    const gapIndex = posOf.get(a.id) ?? 0;
    const gapEndIndex = posOf.get(b.id) ?? gapIndex + 1;

    let hasTransfer = false;
    let hasLodging = false;
    for (let idx = gapIndex + 1; idx < gapEndIndex; idx++) {
      const between = s.library[segIds[idx]];
      if (!between) continue;
      if (between.kind === 'transfer') hasTransfer = true;
      if (between.kind === 'lodging') hasLodging = true;
    }

    const sameAirport = a.to === b.from;
    const arriveUtc = toUtcMillis(a.arriveLocal, a.toTz);
    const departUtc = toUtcMillis(b.departLocal, b.fromTz);
    const layoverMin =
      arriveUtc !== null && departUtc !== null
        ? Math.round((departUtc - arriveUtc) / 60000)
        : null;

    const connWarnings: TripWarning[] = [];
    if (!sameAirport && !hasTransfer) {
      connWarnings.push({
        code: 'need_transfer',
        level: 'warn',
        message: t('calcwarn.need_transfer', { from: a.to, to: b.from }),
        gapIndex,
      });
    }
    if (layoverMin !== null) {
      if (layoverMin < 0) {
        connWarnings.push({
          code: 'negative_connection',
          level: 'error',
          message: t('calcwarn.negative_connection'),
          gapIndex,
        });
      } else if (layoverMin < RISKY_CONNECTION_MIN) {
        // Наложение рейсов — ошибка (красным), нехватка запаса до двух часов — предупреждение.
        connWarnings.push({
          code: 'risky_connection',
          level: 'warn',
          message: t('calcwarn.risky_connection', { duration: formatDuration(layoverMin, t) }),
          gapIndex,
        });
      }
      // Пребывание (сутки и больше в одном аэропорту) — не ожидание рейса:
      // человек так и планировал, предупреждать его не о чем.
      const isStay = sameAirport && layoverMin >= STAY_THRESHOLD_MIN;
      if (layoverMin > LONG_LAYOVER_MIN && !hasLodging && !isStay) {
        connWarnings.push({
          code: 'long_layover',
          level: 'warn',
          message: t('calcwarn.long_layover'),
          gapIndex,
        });
      }
    }

    connections.push({
      gapIndex,
      fromSegmentId: a.id,
      toSegmentId: b.id,
      layoverMin,
      hasTransfer,
      hasLodging,
      sameAirport,
      warnings: connWarnings,
    });

    for (const w of connWarnings) {
      pushWarning(gapIndex + 0.5, w);
    }
  }

  orderedWarnings.sort((a, b) => a.order - b.order);
  const warnings = orderedWarnings.map((ow) => ow.warning);

  return {
    variantId: v.id,
    totalMinor,
    totalWithBaggageMinor,
    breakdown,
    currency: s.displayCurrency,
    converted,
    flightTimeMin,
    doorToDoorMin,
    nights,
    warnings,
    connections,
    perSegmentMinor,
  };
}
